import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Music, Video, Palette, ArrowRight, ArrowUpRight, CheckCircle2, Users2, Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchUserNotifications } from '../lib/notificationService';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import SEO from './common/SEO';
import logo from '../assets/SenritsuXwordmarkwhite.png';
import heroVisual from '../assets/hero-visual.png';
import '../styles/Home.css';

/* Skeleton placeholder shown during initial load */
const HeroSkeleton = () => (
  <div className="hero-content-wrapper">
    <div className="hero-text">
      <span className="skeleton skeleton-title" style={{ marginBottom: '1rem', display: 'block', width: '80%' }} />
      <span className="skeleton skeleton-title" style={{ marginBottom: '1rem', display: 'block', width: '60%' }} />
      <span className="skeleton skeleton-text" style={{ marginBottom: '0.6rem', display: 'block' }} />
      <span className="skeleton skeleton-text short" style={{ marginBottom: '2rem', display: 'block' }} />
      <span className="skeleton skeleton-btn" style={{ display: 'block' }} />
    </div>
    <div className="hero-image-box">
      <span className="skeleton hero-visual-skeleton" style={{ width: '100%', maxWidth: '360px', aspectRatio: '16 / 9', display: 'block', borderRadius: '20px' }} />
    </div>
  </div>
);

export default function Home() {
  const [loaded, setLoaded] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 350);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadNotifs() {
      if (user?.id) {
        try {
          const list = await fetchUserNotifications(user.id);
          if (isMounted) {
            setUnreadNotifs(list.filter((n) => !n.is_read));
          }
        } catch (err) {
          console.warn('[Home] Notifs notice:', err);
        }
      }
    }
    loadNotifs();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const coreFeatures = [
    {
      icon: <Radio size={32} strokeWidth={1.5} />,
      title: 'Global Distribution',
      desc: 'Deliver your songs & music videos to Spotify, Apple Music, VEVO, and 150+ streaming platforms with full artist\u00A0control.',
    },
    {
      icon: <Users2 size={32} strokeWidth={1.5} />,
      title: 'Artist Presence & Collaborations',
      desc: 'Build your verified public artist presence on Artist WEB, discover talent, and collaborate directly with fellow creators.',
    },
    {
      icon: <Video size={32} strokeWidth={1.5} />,
      title: 'Video & Visuals',
      desc: 'Collaborate with verified video editors for music videos, dynamic lyric visualizers, and viral promo\u00A0reels.',
    },
    {
      icon: <Palette size={32} strokeWidth={1.5} />,
      title: 'Cover Art & Branding',
      desc: 'Get eye-catching, DSP-compliant album artwork, Spotify canvases, and promotional design kits.',
    },
  ];

  return (
    <div className="senritsu-container">
      <SEO
        title="SenritsuX | Music Distribution & Artist Services"
        description="SenritsuX is an independent music distribution and artist services platform helping artists distribute music, manage releases, build their artist presence, and grow their creative career."
        canonical="/"
        keywords={[
          'SenritsuX',
          'music distribution',
          'independent artists',
          'music distribution platform',
          'artist services',
          'music releases',
          'artist profiles',
          'Spotify distribution',
          'Apple Music distribution',
        ]}
      />
      <Navbar />

      <main>
        {/* Unread Collab / Notification banner for authenticated users */}
        {isAuthenticated && unreadNotifs.length > 0 && !bannerDismissed && (
          <div className="home-notification-banner-container">
            <div className="senritsu-notification-strip">
              <div className="senritsu-notif-left">
                <div className="senritsu-notif-bell-badge">
                  <Bell size={14} color="#111" />
                </div>
                <div className="senritsu-notif-message">
                  <strong>You have unread collaboration proposals</strong>
                  <span className="senritsu-notif-subtext">
                    Artists are reaching out to collaborate with you on SenritsuX.
                  </span>
                </div>
              </div>
              <div className="senritsu-notif-right">
                <Link to="/dashboard" className="senritsu-notif-cta">
                  <span>View in Dashboard</span>
                  <ArrowRight size={13} />
                </Link>
                <button
                  type="button"
                  onClick={() => setBannerDismissed(true)}
                  className="senritsu-notif-dismiss"
                  aria-label="Dismiss banner"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ── Hero Section ── */}
        <section className={`hero-section ${unreadNotifs.length > 0 && !bannerDismissed ? 'hero-with-banner' : ''}`}>
          {!loaded ? (
            <HeroSkeleton />
          ) : (
            <div className="hero-content-wrapper">
              {/* LEFT: headline + subtitle + CTA */}
              <div className="hero-text">
                <h1 className="hero-title">
                  <span className="hero-title-line">Your Gateway</span>
                  <span className="hero-title-line">To Be An</span>
                  <span className="hero-title-line hero-highlight">ARTIST</span>
                </h1>
                <p className="hero-subtitle">
                  <span className="brand-font">SenritsuX</span> is an independent music distribution and artist services platform that helps artists distribute music, manage releases, build their artist presence, and grow their creative&nbsp;career.
                </p>
                <div className="hero-cta-group">
                  <Link
                    to="/distribution?mode=new"
                    className="cta-button"
                    id="hero-cta-primary"
                  >
                    Submit Your Track
                  </Link>
                </div>
              </div>

              {/* RIGHT: 16:9 hero branding visual */}
              <div className="hero-image-box">
                <div className="hero-visual-card">
                  <img
                    src={heroVisual}
                    alt="SenritsuX by Omniverse"
                    className="hero-visual-img"
                    width="360"
                    height="202"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Glowing accent separator */}
        <div className="glow-line" aria-hidden="true" />

        {/* ── Short Introduction Section ── */}
        <section className="home-intro-section">
          <div className="home-intro-card">
            <div className="home-intro-header">
              <span className="mission-tag">Why <span className="brand-font">SenritsuX</span></span>
              <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '0.8rem' }}>
                Everything a Modern Creator Needs in One&nbsp;Place
              </h2>
              <p className="home-intro-desc">
                From your bedroom recordings to global playlists, we provide the tools, distribution,
                and network to turn your music into a thriving career, without losing your master rights.
              </p>
            </div>

            <div className="home-intro-points">
              <div className="intro-point">
                <CheckCircle2 size={20} color="#d9fb06" />
                <span>100% Rights & Transparent Payouts</span>
              </div>
              <div className="intro-point">
                <CheckCircle2 size={20} color="#d9fb06" />
                <span>Multi-Role Network (Singers, Producers, Editors, Designers)</span>
              </div>
              <div className="intro-point">
                <CheckCircle2 size={20} color="#d9fb06" />
                <span>Curated Playlist Editorial Pitching</span>
              </div>
              <div className="intro-point">
                <CheckCircle2 size={20} color="#d9fb06" />
                <span>Public Portfolios via Artist WEB</span>
              </div>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <Link to="/about" className="link-with-arrow">
                Read More About <span className="brand-font">SenritsuX</span> <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        <div className="glow-line" aria-hidden="true" />

        {/* ── Key Services Highlights ── */}
        <section className="home-features-section">
          <div className="section-header-centered">
            <h2 className="section-title">
              Core Platform&nbsp;<span className="title-highlight">Services</span>
            </h2>
            <p className="section-subtitle">
              Tailored creative solutions built specifically for independent musicians and creators.
            </p>
          </div>

          <div className="features-grid">
            {coreFeatures.map((feat, idx) => (
              <div key={idx} className="feature-card">
                <div className="feature-icon">{feat.icon}</div>
                <h3 className="feature-title">{feat.title}</h3>
                <p className="feature-description">{feat.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/services" className="cta-button">
              View All Services &amp; Pricing <ArrowRight size={16} style={{ marginLeft: 6, display: 'inline-block' }} />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
